import { generatePath } from "react-router";

import { paths } from "app/constants/paths";

import type { CloudProvider } from "./__generated__/CloudOrganizationCard_organization.graphql";

/** Path of the identity providers list page. */
export const IDENTITY_PROVIDERS_PATH = paths.identityProviders;

/**
 * Tooltip copy for the add affordances — the list page's action bar and the add
 * form's save action — which are disabled without system edit permission.
 */
export const ADD_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to add an identity provider";

/**
 * Path of a SAML provider's details page. SAML providers are addressed by their
 * Cognito provider name, which is unique and immutable but caller-supplied;
 * `generatePath` URI-encodes it rather than interpolating raw.
 *
 * The kind is part of the path because the two kinds of provider are addressed
 * differently — a SAML provider by name, a cloud organization by provider and
 * key — and neither identifier space can be told from the other on sight.
 */
export function samlProviderPath(name: string) {
  return generatePath(paths.samlProviderDetails, {
    providerName: name,
  });
}

/** Path of a SAML provider's edit form. */
export function samlProviderEditPath(name: string) {
  return generatePath(paths.samlProviderEdit, {
    providerName: name,
  });
}

/**
 * The URL segment naming each cloud provider, and the single source of truth
 * for the mapping in both directions. Partial because the server may return a
 * provider added after this build; see {@link providerSegment} for the fallback.
 */
const PROVIDER_SEGMENTS: Partial<Record<CloudProvider, string>> = {
  AWS: "aws",
  Azure: "azure",
  GCP: "gcp",
  Kubernetes: "kubernetes",
  TencentCloud: "tencentcloud",
};

/** The inverse of {@link PROVIDER_SEGMENTS}, for reading a segment back. */
const SEGMENT_PROVIDERS = new Map(
  Object.entries(PROVIDER_SEGMENTS).map(([provider, segment]) => [
    segment,
    provider as CloudProvider,
  ]),
);

/**
 * The URL segment for a cloud provider. A provider the map does not know falls
 * back to the lower-cased enum value, so a link still says which provider it
 * meant.
 */
function providerSegment(provider: CloudProvider) {
  return PROVIDER_SEGMENTS[provider] ?? provider.toLowerCase();
}

/**
 * Path of a cloud organization's details page. Organizations are addressed by
 * the (provider, key) pair that identifies them in the API; the provider is
 * written as its URL segment and mapped back by {@link cloudProviderFromParam}.
 */
export function cloudOrganizationPath(provider: CloudProvider, key: string) {
  return generatePath(paths.cloudOrganizationDetails, {
    provider: providerSegment(provider),
    key: key,
  });
}

/** Path of a cloud organization's edit form. */
export function cloudOrganizationEditPath(
  provider: CloudProvider,
  key: string,
) {
  return generatePath(paths.cloudOrganizationEdit, {
    provider: providerSegment(provider),
    key: key,
  });
}

/**
 * Reads the `:provider` route param back into a `CloudProvider`, or null if the
 * segment names none.
 */
export function cloudProviderFromParam(param: string | undefined) {
  return param ? (SEGMENT_PROVIDERS.get(param.toLowerCase()) ?? null) : null;
}

/**
 * Tooltip copy for the edit affordances — the details page's edit action and
 * the edit form's save action — which are disabled without system edit
 * permission.
 */
export const EDIT_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to edit an identity provider";

/**
 * Tooltip copy for the details page's sync action, which is disabled without
 * system edit permission.
 */
export const SYNC_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to sync an identity provider";

/**
 * Tooltip copy for the details page's remove action, which is disabled without
 * system edit permission.
 */
export const REMOVE_PROVIDER_PERMISSION_MESSAGE =
  "You need system edit permission to remove an identity provider";
